export type Configuration = {
  id: string
  name: string
  description: string
  value: string
}

export type Approval = {
  id: string
  library: string
  site: string
  fileName: string
  status: string
  timestamp: string
  sizeMb: number
}

export type Prompt = {
  id: string
  title: string
  prompt: string
  template: string
}

export type PromptItem = {
  title: string
  prompt: string
}

export type Template = {
  id: string
  name: string
  source: string
  webConfig: string
  status: string
  prompts: PromptItem[]
  fields: Field[]
  docxFile: string
}


export type Field = {
  name: string
  type: string
  mapping: FieldMapping
}

export type FieldMapping = {
  // For fields like Region which are just a list of values
  list: string[]          // Example: ["SEET", "SEMED", "EE", "CA"]

  parentField: string

  // For fields like Country, where there's a nested mapping based on selected Region
  nestedFields: {        // Maps regions to their corresponding countries
    [key: string]: string[]  // E.g., "SEMED": ["Egypt", "Jordan"]
  }
}

export type Library = {
  id: string
  name: string
  url: string
}

export type AskResponse = {
  answer: string | []
  citations: Citation[]
  generated_chart: string | null
  error?: string
  message_id?: string
  feedback?: Feedback
  exec_results?: ExecResults[]
}

export type Citation = {
  part_index?: number
  content: string
  id: string
  title: string | null
  filepath: string | null
  url: string | null
  metadata: string | null
  chunk_id: string | null
  reindex_id: string | null
}

export type ToolMessageContent = {
  citations: Citation[]
  intent: string
}

export type AzureSqlServerExecResult = {
  intent: string
  search_query: string | null
  search_result: string | null
  code_generated: string | null
  code_exec_result?: string | undefined
}

export type AzureSqlServerExecResults = {
  all_exec_results: AzureSqlServerExecResult[]
}

export type ChatMessage = {
  id: string
  role: string
  content: string | [{ type: string; text: string }, { type: string; image_url: { url: string } }]
  source?: string
  sys_message?: string
  tokens?: number
  temperature?: number
  search_web?: boolean
  search_trusted_web?: boolean
  doc_wizard?: boolean
  template?: string
  fieldValues?: { [key: string]: string }
  first_draft?: boolean
  end_turn?: boolean
  date: string
  feedback?: Feedback
  context?: string
}

export type ExecResults = {
  intent: string
  search_query: string | null
  search_result: string | null
  code_generated: string | null
}

export type Conversation = {
  id: string
  title: string
  messages: ChatMessage[]
  date: string
}

export enum ChatCompletionType {
  ChatCompletion = 'chat.completion',
  ChatCompletionChunk = 'chat.completion.chunk'
}

export type ChatResponseChoice = {
  messages: ChatMessage[]
}

export type ChatResponse = {
  id: string
  model: string
  created: number
  object: ChatCompletionType
  choices: ChatResponseChoice[]
  history_metadata: {
    conversation_id: string
    title: string
    date: string
  }
  error?: any
}

export type ConversationRequest = {
  messages: ChatMessage[]
}

export type UserInfo = {
  access_token: string
  expires_on: string
  id_token: string
  provider_name: string
  user_claims: any[]
  user_id: string
}

export enum CosmosDBStatus {
  NotConfigured = 'CosmosDB is not configured',
  NotWorking = 'CosmosDB is not working',
  InvalidCredentials = 'CosmosDB has invalid credentials',
  InvalidDatabase = 'Invalid CosmosDB database name',
  InvalidContainer = 'Invalid CosmosDB container name',
  Working = 'CosmosDB is configured and working'
}

export type CosmosDBHealth = {
  cosmosDB: boolean
  status: string
}

export enum ChatHistoryLoadingState {
  Loading = 'loading',
  Success = 'success',
  Fail = 'fail',
  NotStarted = 'notStarted'
}

export type ErrorMessage = {
  title: string
  subtitle: string
}

export type UI = {
  title: string
  chat_title: string
  chat_description: string
  wizard_title?: string
  wizard_description?: string
  logo?: string
  chat_logo?: string
  show_share_button?: boolean
  show_web_search_button?: boolean
  environment?: string
  list_of_trusted_websites?: string
}

export type FrontendSettings = {
    auth_enabled?: string | null
    feedback_enabled?: string | null
    ui?: UI
    sanitize_answer?: boolean
    oyd_enabled?: boolean
}

export type EnvironmentVariablesList = {
  environment_variables?: EnvironmentVariables;
}

export type EnvironmentVariables = {
    admin?: boolean
    tokens?: number
    temperature: number
    sys_message: string
    source: string
    max_upload_size: number
}

export type TemplatesList = {
  templates_list?: TemplateItem[];
}

export type TemplateItem = {
  name: string;
  fields: FieldItem[];
}

export type FieldItem = {
  options: string[]
  name: string
  type: string
}



export type CountryList = {
  country_list?: Country[];
}
export type Country = {
  country : string
}

export type IndexesList = {
  indexes_list?: Index[];
}
export type Index = {
  key: string;
  text: string;
}

export enum Feedback {
  Neutral = 'neutral',
  Positive = 'positive',
  Negative = 'negative',
  MissingCitation = 'missing_citation',
  WrongCitation = 'wrong_citation',
  OutOfScope = 'out_of_scope',
  InaccurateOrIrrelevant = 'inaccurate_or_irrelevant',
  OtherUnhelpful = 'other_unhelpful',
  HateSpeech = 'hate_speech',
  Violent = 'violent',
  Sexual = 'sexual',
  Manipulative = 'manipulative',
  OtherHarmful = 'other_harmlful'
}
